import { type AgentModeKey, Scope } from '@developerz/domain';
import { Index, type JSX, Match, Show, Switch } from 'solid-js';
import { createStore } from 'solid-js/store';
import { t } from '../i18n';
import {
  type AgentModesScope,
  type AgentModeView,
  canReset,
  putAgentMode,
  resetAgentMode,
  useAgentModes,
  withTool,
} from '../lib/agent-modes';
import { errorReason } from '../lib/api';
import { createKeyedMutation } from '../lib/keyed-mutation';
import { useScopeVerdict } from '../lib/scope-gate';
import { AgentModeRow } from './AgentModeRow';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';

// Agent modes (plan 101 slice 06) — CONTAINER, mounted on the Prompts page (the
// org rows) and on a repo's Settings tab (that repo's override). Reads the
// resolved modes, writes one row per act, and re-reads after every write: the
// server is the arbiter, so its 422 `detail` renders in the refused row and
// the next GET is what the rows show. `<AgentModeRow/>` renders.
//
// Per-row lock via `createKeyedMutation` (one mode's write never drops a
// neighbour's). The failure is kept PER MODE rather than on the kit's one
// surface-wide `error()`, because a refusal names a mode's own tool list and
// read under another row it would describe the wrong thing.
//
// Every route here needs `write:prompts` (`apps/api/src/routes/agent-modes.ts`),
// GET included, and a member/viewer does not hold it (#7426: the Prompts page
// sent a member's `GET /v1/agent-modes` only to collect its 403). So the read
// waits on `/v1/me`'s scope list (`lib/scope-gate.ts`): a credential that lacks
// the scope sends nothing and the panel says why; an unsettled one shows the
// skeleton.

export function AgentModesPanel(props: {
  readonly scope: AgentModesScope;
  readonly token: string | null;
}): JSX.Element {
  const verdict = useScopeVerdict(Scope.WritePrompts, props.token);
  const modes = useAgentModes(
    () => props.scope,
    props.token,
    () => verdict() === 'allowed',
  );
  const lock = createKeyedMutation({ fallback: t('agent_modes.save_error') });
  const [errors, setErrors] = createStore<Partial<Record<AgentModeKey, string | null>>>({});

  const write = (key: AgentModeKey, act: () => Promise<unknown>, fallback: string): void => {
    void lock.run(key, async () => {
      setErrors(key, null);
      try {
        await act();
      } catch (err) {
        setErrors(key, errorReason(err, fallback));
      } finally {
        await modes.refetch();
      }
    });
  };

  const save = (view: AgentModeView, enabled: boolean, codeTools: AgentModeView['code_tools']) =>
    write(
      view.key,
      () =>
        putAgentMode(
          props.scope,
          { mode_key: view.key, enabled, code_tools: codeTools },
          props.token,
        ),
      t('agent_modes.save_error'),
    );

  const resetLabel = (view: AgentModeView): string | null =>
    canReset(props.scope, view)
      ? t(props.scope.kind === 'repo' ? 'agent_modes.reset_org' : 'agent_modes.reset_default')
      : null;

  // The rows are built ONCE, here, and the boundary's data slot hands back this same
  // node every time (#6755). `QueryBoundary` re-runs that slot on every re-read, and
  // `For` keys rows by object identity while a re-read mints new objects, so each
  // write re-created every row: the switch a keyboard user had just pressed lost its
  // node, focus fell to <body>, and the next Tab restarted at the top of the list.
  // `Index` keys by position, as `CapabilitySwitches` does: the API lists a scope's
  // writable keys in one fixed order, so a row keeps its node and only its `view`
  // moves. The same node handed back is a no-op insert (`value === current`), so the
  // list is never re-mounted either.
  const rows = (
    <ul class="agent-modes__list">
      <Index each={modes.data}>
        {(view) => (
          <AgentModeRow
            view={view()}
            idPrefix={`agent-mode-${props.scope.kind}-${view().key}`}
            busy={lock.isBusy(view().key)}
            error={errors[view().key] ?? null}
            resetLabel={resetLabel(view())}
            onToggle={() => save(view(), !view().enabled, view().code_tools)}
            onTool={(tool, on) => save(view(), view().enabled, withTool(view(), tool, on))}
            onReset={() =>
              write(
                view().key,
                () => resetAgentMode(props.scope, view().key, props.token),
                t('agent_modes.reset_error'),
              )
            }
          />
        )}
      </Index>
    </ul>
  );

  return (
    <Panel title={t('agent_modes.title')} class="agent-modes">
      <p class="card__desc">{t('agent_modes.desc')}</p>
      <Show when={props.scope.kind === 'repo'}>
        <p class="card__desc">{t('agent_modes.desc_repo')}</p>
      </Show>
      <Switch fallback={<Skeleton height="12rem" />}>
        <Match when={verdict() === 'denied'}>
          <p class="list-empty" role="status">
            {t('agent_modes.denied')}
          </p>
        </Match>
        <Match when={verdict() === 'allowed'}>
          <QueryBoundary query={modes} loading={<Skeleton height="12rem" />}>
            {() => rows}
          </QueryBoundary>
        </Match>
      </Switch>
    </Panel>
  );
}
